import {
  FormEvent,
  MouseEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  approveProcessChart,
  createProcessChart,
  deleteProcessChart,
  deleteProcessChartAttachment,
  downloadProcessChartAttachment,
  fetchProcessChart,
  fetchProcessCharts,
  revokeProcessChart,
  updateProcessChart,
  uploadProcessChartAttachments,
  downloadProcessChartPdf,
} from "../../api/processChart";
import { useCompanyStore } from "../../store/companyStore";
import { APPROVAL_TARGET_PROCESS_CHART } from "../../types/approval";
import { canConfirmInspection } from "../../types/companyRole";
import { ProcessChart, ProcessChartAttachment } from "../../types/processChart";
import { notifyError, reportError } from "../../utils/notify";
import {
  ApprovalPendingFields,
  isApprovalInProgress,
  withApprovalPendingClass,
  withApprovalPendingLabel,
} from "../../utils/approvalPending";
import ApprovalCancelButton from "../approval/ApprovalCancelButton";
import ApprovalRequestModal from "../approval/ApprovalRequestModal";

import "../quality/EducationAnnualPlanTab.css";
import "../quality/QualityShell.css";
import "./InspectionFormSheet.css";
import "./NonconformanceReportPanel.css";

function formatDate(value?: string | null) {
  if (!value) return "-";
  return value.slice(0, 10);
}

function formatSize(size?: number | null) {
  if (size == null) return "";
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

function apiErrorMessage(err: unknown, fallback: string) {
  const message = (err as { response?: { data?: { message?: string } } })
    ?.response?.data?.message;
  return message?.trim() || fallback;
}

function isApproved(status?: string | null) {
  return (status || "").toUpperCase() === "APPROVED";
}

function isDraft(status?: string | null) {
  const s = (status || "DRAFT").toUpperCase();
  return s === "DRAFT" || s === "";
}

function StampBox({
  authorName,
  reviewerName,
  approverName,
}: {
  authorName?: string | null;
  reviewerName?: string | null;
  approverName?: string | null;
}) {
  return (
    <div className="ifs-sheet__stamps">
      {(
        [
          ["작성", authorName],
          ["검토", reviewerName],
          ["승인", approverName],
        ] as const
      ).map(([label, value]) => (
        <div key={label}>
          <strong>{label}</strong>
          <span
            style={{
              color: value ? "#1f2a37" : "#98a2b3",
              fontWeight: value ? 600 : 400,
            }}
          >
            {value || "-"}
          </span>
        </div>
      ))}
    </div>
  );
}

type DocView =
  | { type: "list" }
  | { type: "create" }
  | { type: "edit"; processChartId: number }
  | { type: "detail"; processChartId: number };

const LIST_VIEW: DocView = { type: "list" };

function ProcessChartPanel({
  contractId = null,
  embedded = false,
}: {
  contractId?: number | null;
  embedded?: boolean;
} = {}) {
  void embedded;
  const companyId = useCompanyStore((s) => s.companyId);
  const role = useCompanyStore((s) => s.role);
  const canApproveDirect = canConfirmInspection(role);
  const fileRef = useRef<HTMLInputElement>(null);

  const [view, setView] = useState<DocView>(LIST_VIEW);
  const [rows, setRows] = useState<ProcessChart[]>([]);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [approving, setApproving] = useState(false);
  const [pdfDownloading, setPdfDownloading] = useState(false);
  const [error, setError] = useState("");
  const [approvalMessage, setApprovalMessage] = useState("");
  const [approvalOpen, setApprovalOpen] = useState(false);

  const [title, setTitle] = useState("");
  const [productName, setProductName] = useState("");
  const [remark, setRemark] = useState("");
  const [status, setStatus] = useState("DRAFT");
  const [authorName, setAuthorName] = useState<string | null>(null);
  const [reviewerName, setReviewerName] = useState<string | null>(null);
  const [approverName, setApproverName] = useState<string | null>(null);
  const [canRevoke, setCanRevoke] = useState(false);
  const [approvalPending, setApprovalPending] = useState<ApprovalPendingFields>(
    {},
  );
  const [attachments, setAttachments] = useState<ProcessChartAttachment[]>([]);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [, setDirty] = useState(false);

  const editId =
    view.type === "edit" || view.type === "detail" ? view.processChartId : null;
  const showForm = view.type !== "list";
  const isDetail = view.type === "detail";
  const isEditing = view.type === "create" || view.type === "edit";
  const locked = !isEditing;

  const attachmentsLocked =
    isApproved(status) ||
    isApprovalInProgress(approvalPending) ||
    (!isEditing && !isDraft(status));

  const applyDetail = (detail: ProcessChart) => {
    setTitle(detail.title || "");
    setProductName(detail.productName || "");
    setRemark(detail.remark || "");
    setStatus(detail.status || "DRAFT");
    setAuthorName(detail.authorName ?? null);
    setReviewerName(detail.reviewerName ?? null);
    setApproverName(detail.approverName ?? null);
    setCanRevoke(detail.canRevoke ?? false);
    setApprovalPending({
      approvalInProgress: detail.approvalInProgress,
      pendingApprovalRequestId: detail.pendingApprovalRequestId,
      canCancelApproval: detail.canCancelApproval,
    });
    setAttachments(detail.attachments || []);
    setDirty(false);
  };

  const loadList = useCallback(
    async (keyword?: string) => {
      try {
        setLoading(true);
        setRows(await fetchProcessCharts(keyword, contractId));
        setError("");
      } catch (err: unknown) {
        reportError(
          setError,
          apiErrorMessage(err, "공정도 목록을 불러오지 못했습니다."),
        );
        setRows([]);
      } finally {
        setLoading(false);
      }
    },
    [contractId],
  );

  useEffect(() => {
    setView(LIST_VIEW);
    setSearchKeyword("");
    void loadList();
  }, [companyId, loadList, setView]);

  useEffect(() => {
    if (showForm) return;
    const timer = window.setTimeout(() => void loadList(searchKeyword), 250);
    return () => window.clearTimeout(timer);
  }, [searchKeyword, loadList, showForm]);

  const resetForm = () => {
    setTitle("");
    setProductName("");
    setRemark("");
    setStatus("DRAFT");
    setAuthorName(null);
    setReviewerName(null);
    setApproverName(null);
    setCanRevoke(false);
    setApprovalPending({});
    setAttachments([]);
    setPendingFiles([]);
    setDirty(false);
    setApprovalMessage("");
    setApprovalOpen(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const openCreate = () => {
    resetForm();
    setDirty(true);
    setView({ type: "create" });
  };

  const openDetail = async (row: ProcessChart) => {
    try {
      setLoading(true);
      setError("");
      const detail = await fetchProcessChart(row.processChartId);
      applyDetail(detail);
      setPendingFiles([]);
      if (fileRef.current) fileRef.current.value = "";
      setView({ type: "detail", processChartId: row.processChartId });
    } catch (err: unknown) {
      reportError(
        setError,
        apiErrorMessage(err, "공정도를 불러오지 못했습니다."),
      );
    } finally {
      setLoading(false);
    }
  };

  const openEditFromDetail = () => {
    if (editId == null || isApproved(status)) return;
    setView({ type: "edit", processChartId: editId });
  };

  const backToList = () => {
    resetForm();
    setView(LIST_VIEW);
    void loadList(searchKeyword);
  };

  const handleSave = async (event: FormEvent) => {
    event.preventDefault();
    if (locked) {
      reportError(setError, "승인된 공정도는 수정할 수 없습니다.");
      return;
    }
    if (!title.trim()) {
      reportError(setError, "제목을 입력해 주세요.");
      return;
    }
    try {
      setSaving(true);
      setError("");
      const payload = {
        title: title.trim(),
        productName: productName.trim() || null,
        remark: remark.trim() || null,
        contractId: contractId ?? undefined,
      };
      let processChartId = editId;
      let saved: ProcessChart;
      if (processChartId != null) {
        saved = await updateProcessChart(processChartId, payload);
      } else {
        saved = await createProcessChart(payload);
        processChartId = saved.processChartId;
      }
      if (pendingFiles.length > 0 && processChartId != null) {
        const uploaded = await uploadProcessChartAttachments(
          processChartId,
          pendingFiles,
        );
        saved = {
          ...saved,
          attachments: [...(saved.attachments || []), ...uploaded],
          attachmentCount: (saved.attachmentCount ?? 0) + uploaded.length,
        };
        setPendingFiles([]);
      }
      applyDetail(saved);
      setPendingFiles([]);
      setDirty(false);
      setView({ type: "detail", processChartId: saved.processChartId });
      await loadList(searchKeyword);
    } catch (err: unknown) {
      reportError(
        setError,
        apiErrorMessage(err, "공정도 저장에 실패했습니다."),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteFromList = async (row: ProcessChart, event: MouseEvent) => {
    event.stopPropagation();
    if (isApproved(row.status)) {
      reportError(setError, "승인된 공정도는 삭제할 수 없습니다.");
      return;
    }
    if (!window.confirm(`"${row.title}"를 삭제할까요?`)) return;
    try {
      await deleteProcessChart(row.processChartId);
      await loadList(searchKeyword);
    } catch (err: unknown) {
      reportError(
        setError,
        apiErrorMessage(err, "공정도 삭제에 실패했습니다."),
      );
    }
  };

  const handleApprove = async () => {
    if (editId == null) return;
    if (!canApproveDirect) {
      reportError(setError, "바로 승인은 오너/팀장만 가능합니다.");
      return;
    }
    if (
      !window.confirm(
        "공정도를 바로 승인할까요? 승인 후에는 수정·삭제할 수 없습니다.",
      )
    ) {
      return;
    }
    try {
      setApproving(true);
      setError("");
      const saved = await approveProcessChart(editId);
      applyDetail(saved);
      setApprovalMessage("바로 승인되었습니다.");
      await loadList(searchKeyword);
    } catch (err: unknown) {
      reportError(setError, apiErrorMessage(err, "바로 승인에 실패했습니다."));
    } finally {
      setApproving(false);
    }
  };

  const handleRevoke = async () => {
    if (editId == null) return;
    if (!window.confirm("승인을 철회할까요?\n승인란·검토란이 지워집니다."))
      return;
    try {
      setApproving(true);
      setError("");
      const saved = await revokeProcessChart(editId);
      applyDetail(saved);
      setApprovalMessage("승인이 철회되었습니다.");
      await loadList(searchKeyword);
    } catch (err: unknown) {
      reportError(setError, apiErrorMessage(err, "승인 철회에 실패했습니다."));
    } finally {
      setApproving(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (editId == null) return;
    try {
      setPdfDownloading(true);
      const name = title?.trim() || productName?.trim() || String(editId);
      await downloadProcessChartPdf(editId, `공정도_${name}.pdf`);
    } catch (err) {
      notifyError(
        err instanceof Error
          ? err.message
          : "PDF 다운로드에 실패했습니다. Gotenberg 실행 여부를 확인해 주세요.",
      );
    } finally {
      setPdfDownloading(false);
    }
  };

  const handleUploadNow = async (files: FileList | null) => {
    if (!files?.length || attachmentsLocked) return;
    const selected = Array.from(files);
    if (fileRef.current) fileRef.current.value = "";
    if (editId == null) {
      setPendingFiles((prev) => [...prev, ...selected]);
      return;
    }
    try {
      setSaving(true);
      const uploaded = await uploadProcessChartAttachments(editId, selected);
      setAttachments((prev) => [...prev, ...uploaded]);
    } catch (err: unknown) {
      reportError(
        setError,
        apiErrorMessage(err, "첨부파일 업로드에 실패했습니다."),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAttachment = async (attachment: ProcessChartAttachment) => {
    if (editId == null || attachmentsLocked) return;
    if (!window.confirm(`"${attachment.fileName}" 첨부를 삭제할까요?`)) return;
    try {
      setSaving(true);
      await deleteProcessChartAttachment(editId, attachment.attachmentId);
      setAttachments((prev) =>
        prev.filter((a) => a.attachmentId !== attachment.attachmentId),
      );
    } catch (err: unknown) {
      reportError(
        setError,
        apiErrorMessage(err, "첨부파일 삭제에 실패했습니다."),
      );
    } finally {
      setSaving(false);
    }
  };

  const renderDetailActions = () => (
    <div
      className="ca-toolbar-actions"
      style={{ display: "flex", gap: 8, flexWrap: "wrap" }}
    >
      {isApproved(status) ? (
        <button
          type="button"
          className="ncr-btn is-primary"
          disabled={pdfDownloading}
          onClick={() => void handleDownloadPdf()}
        >
          {pdfDownloading ? "PDF 생성 중…" : "PDF 다운로드"}
        </button>
      ) : null}
      {canRevoke ? (
        <button
          type="button"
          className="ncr-btn is-danger"
          disabled={saving || approving}
          onClick={() => void handleRevoke()}
        >
          {approving ? "철회 중..." : "승인 철회"}
        </button>
      ) : null}
      {isDraft(status) && !isApprovalInProgress(approvalPending) ? (
        <>
          <button
            type="button"
            className="ncr-btn is-secondary"
            onClick={openEditFromDetail}
          >
            수정
          </button>
          <button
            type="button"
            className="ncr-btn is-primary"
            disabled={saving || approving}
            onClick={() => {
              setApprovalMessage("");
              setApprovalOpen(true);
            }}
          >
            결재 요청
          </button>
          {canApproveDirect ? (
            <button
              type="button"
              className="ncr-btn is-approve"
              disabled={saving || approving}
              onClick={() => void handleApprove()}
            >
              {approving ? "승인 중..." : "승인"}
            </button>
          ) : null}
          <button
            type="button"
            className="ncr-btn is-danger"
            disabled={saving || approving}
            onClick={() => {
              if (editId == null) return;
              if (!window.confirm(`"${title}"를 삭제할까요?`)) return;
              void deleteProcessChart(editId).then(() => {
                backToList();
              });
            }}
          >
            삭제
          </button>
        </>
      ) : null}
      {isApprovalInProgress(approvalPending) ? (
        <ApprovalCancelButton
          detail={approvalPending}
          className="ncr-btn is-ghost"
          disabled={saving || approving}
          onCancelled={async () => {
            if (editId == null) return;
            const detail = await fetchProcessChart(editId);
            applyDetail(detail);
            void loadList(searchKeyword);
          }}
        />
      ) : null}
      <button
        type="button"
        className="ncr-btn is-secondary"
        disabled={saving || approving}
        onClick={backToList}
      >
        목록
      </button>
    </div>
  );

  const renderEditActions = () => (
    <div className="quality-form__actions">
      <button
        type="submit"
        className="ncr-btn is-primary"
        disabled={saving || approving}
      >
        {saving ? "저장 중..." : "저장"}
      </button>
      <button
        type="button"
        className="ncr-btn is-secondary"
        disabled={saving || approving}
        onClick={() =>
          view.type === "edit" && editId != null
            ? setView({ type: "detail", processChartId: editId })
            : backToList()
        }
      >
        {view.type === "edit" ? "상세" : "목록"}
      </button>
    </div>
  );

  if (showForm) {
    return (
      <main className="quality-shell sg-panel">
        <form
          className="quality-form"
          onSubmit={
            isEditing ? (e) => void handleSave(e) : (e) => e.preventDefault()
          }
        >
          {isDetail ? (
            <div
              className="quality-shell__toolbar"
              style={{ marginBottom: 12 }}
            >
              <div>
                <h3 style={{ margin: 0 }}>공정도 상세</h3>
                <p className="quality-form__hint" style={{ margin: "4px 0 0" }}>
                  {title || "-"} ·{" "}
                  {withApprovalPendingLabel(
                    isApproved(status) ? "승인" : "작성중",
                    approvalPending.approvalInProgress,
                  )}
                </p>
              </div>
              {renderDetailActions()}
            </div>
          ) : (
            renderEditActions()
          )}

          {error ? <p className="quality-error">{error}</p> : null}
          {approvalMessage ? (
            <p className="quality-form__hint">{approvalMessage}</p>
          ) : null}

          <section className="quality-form__section">
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: 16,
                alignItems: "flex-start",
                marginBottom: 12,
              }}
            >
              <h3 style={{ margin: 0 }}>
                {view.type === "create"
                  ? "공정도 등록"
                  : view.type === "edit"
                    ? "공정도 수정"
                    : "공정도"}
              </h3>
              {isEditing ? (
                <p className="quality-form__hint" style={{ margin: "4px 0 0" }}>
                  저장하면 상세보기로 이동합니다.
                </p>
              ) : null}
              <StampBox
                authorName={authorName}
                reviewerName={reviewerName}
                approverName={approverName}
              />
            </div>
            <div className="quality-form__grid">
              <label className="is-span-2">
                제목 *
                <input
                  value={title}
                  onChange={(e) => {
                    setTitle(e.target.value);
                    setDirty(true);
                  }}
                  placeholder="예: 조립 공정 공정도"
                  maxLength={255}
                  required
                  readOnly={locked}
                />
              </label>
              <label className="is-span-2">
                관련 제품
                <input
                  value={productName}
                  onChange={(e) => {
                    setProductName(e.target.value);
                    setDirty(true);
                  }}
                  placeholder="제품명 입력"
                  maxLength={255}
                  readOnly={locked}
                />
              </label>
              <label className="is-span-2">
                비고
                <textarea
                  rows={3}
                  value={remark}
                  onChange={(e) => {
                    setRemark(e.target.value);
                    setDirty(true);
                  }}
                  placeholder="메모"
                  maxLength={2000}
                  readOnly={locked}
                />
              </label>
            </div>
          </section>

          <section className="quality-form__section">
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 12,
                marginBottom: 8,
              }}
            >
              <h3 style={{ margin: 0 }}>첨부파일</h3>
              {!attachmentsLocked ? (
                <button
                  type="button"
                  className="ncr-btn is-secondary"
                  disabled={saving || approving}
                  onClick={() => fileRef.current?.click()}
                >
                  파일 추가
                </button>
              ) : null}
              <input
                ref={fileRef}
                type="file"
                multiple
                hidden
                onChange={(e) => void handleUploadNow(e.target.files)}
              />
            </div>
            <p className="quality-form__hint">
              도면·공정표 등을 첨부할 수 있습니다. (최대 20개, 각 20MB)
              {editId == null ? " 신규는 저장 시 함께 업로드됩니다." : ""}
            </p>
            <ul className="edu-plan-files">
              {attachments.map((file) => (
                <li key={file.attachmentId}>
                  <div>
                    <strong>{file.fileName}</strong>
                    <span>{formatSize(file.fileSize)}</span>
                  </div>
                  <div className="edu-plan-files__actions">
                    <button
                      type="button"
                      className="ncr-btn is-secondary"
                      onClick={() =>
                        void downloadProcessChartAttachment(
                          editId!,
                          file.attachmentId,
                          file.fileName,
                        ).catch(() =>
                          reportError(
                            setError,
                            "첨부파일 다운로드에 실패했습니다.",
                          ),
                        )
                      }
                    >
                      다운로드
                    </button>
                    {!attachmentsLocked ? (
                      <button
                        type="button"
                        className="ncr-btn is-danger"
                        disabled={saving || approving}
                        onClick={() => void handleDeleteAttachment(file)}
                      >
                        삭제
                      </button>
                    ) : null}
                  </div>
                </li>
              ))}
              {pendingFiles.map((file, idx) => (
                <li key={`pending-${file.name}-${idx}`}>
                  <div>
                    <strong>{file.name}</strong>
                    <span>저장 대기 · {formatSize(file.size)}</span>
                  </div>
                  <button
                    type="button"
                    className="ncr-btn is-danger"
                    onClick={() =>
                      setPendingFiles((prev) =>
                        prev.filter((_, i) => i !== idx),
                      )
                    }
                  >
                    제거
                  </button>
                </li>
              ))}
              {attachments.length === 0 && pendingFiles.length === 0 ? (
                <li className="is-empty">등록된 첨부파일이 없습니다.</li>
              ) : null}
            </ul>
          </section>

          {isDetail ? (
            <div className="quality-form__actions">{renderDetailActions()}</div>
          ) : (
            renderEditActions()
          )}

          {editId != null ? (
            <ApprovalRequestModal
              open={approvalOpen}
              titleDefault={`${title || "공정도"} 결재`}
              targetType={APPROVAL_TARGET_PROCESS_CHART}
              targetId={editId}
              onClose={() => setApprovalOpen(false)}
              onSubmitted={() => {
                setApprovalOpen(false);
                setApprovalMessage(
                  "결재 요청이 등록되었습니다. 헤더의 결재 탭에서 확인할 수 있습니다.",
                );
                if (editId != null) {
                  void fetchProcessChart(editId).then(applyDetail);
                }
                void loadList(searchKeyword);
              }}
            />
          ) : null}
        </form>
      </main>
    );
  }

  return (
    <main className="quality-shell sg-panel">
      <div className="quality-shell__toolbar">
        <div>
          <h2>{contractId != null ? "계약 공정도" : "공정도"}</h2>
          <p>제조 QC 공정 문서를 첨부 파일로 관리합니다.</p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input
            type="search"
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
            placeholder="제목·제품·비고 검색"
            style={{
              height: 34,
              padding: "0 12px",
              border: "1px solid #cbd5e1",
              borderRadius: 8,
              minWidth: 200,
              fontSize: 13,
            }}
          />
          <button
            type="button"
            className="ncr-btn is-primary"
            onClick={openCreate}
          >
            등록
          </button>
        </div>
      </div>

      {error ? <p className="quality-error">{error}</p> : null}

      <div className="quality-table-wrap">
        <table className="quality-table">
          <thead>
            <tr>
              <th>제목</th>
              <th style={{ width: 200 }}>관련 제품</th>
              <th style={{ width: 72 }}>첨부</th>
              <th style={{ width: 88 }}>상태</th>
              <th style={{ width: 120 }}>등록일</th>
              <th style={{ width: 80 }}>관리</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6}>불러오는 중…</td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={6}>등록된 공정도가 없습니다.</td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr
                  key={row.processChartId}
                  onClick={() => void openDetail(row)}
                  style={{ cursor: "pointer" }}
                >
                  <td>{row.title}</td>
                  <td>{row.productName || "-"}</td>
                  <td>{row.attachmentCount ?? 0}</td>
                  <td>
                    <span
                      className={`quality-badge is-${withApprovalPendingClass(
                        isApproved(row.status) ? "approved" : "draft",
                        row.approvalInProgress,
                      )}`}
                    >
                      {withApprovalPendingLabel(
                        isApproved(row.status) ? "승인" : "작성중",
                        row.approvalInProgress,
                      )}
                    </span>
                  </td>
                  <td>{formatDate(row.createdAt)}</td>
                  <td>
                    {!isApproved(row.status) && !isApprovalInProgress(row) ? (
                      <button
                        type="button"
                        className="ncr-btn is-danger"
                        onClick={(e) => void handleDeleteFromList(row, e)}
                      >
                        삭제
                      </button>
                    ) : (
                      "-"
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </main>
  );
}

export default ProcessChartPanel;
